import React, {
  useCallback, useEffect, useState,
} from 'react';
import {
  Box, BoxProps, styled, useTheme,
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { ChartData } from 'chart.js';
import { useIntl } from 'react-intl';
import { BillEstimateResponse, ServicePoint } from '../../models';
import { mapPlanToPalette } from '../../AppTheme';
import { IntlService } from '../../services';

const ChartWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  position: 'relative',
  '& > canvas': {
    position: 'absolute',
    left: 0,
    top: 0,
    PointerEvent: 'none',
  },
}));

const ChartAreaWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    width: '300px',
    overflowX: 'scroll',
  },
}));

const ChartInnerAreaWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minHeight: 300,
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    width: '750px',
  },
}));

interface BillChartProps {
  resp: BillEstimateResponse[] | undefined;
  selectedServicePoint: ServicePoint | undefined;
}

function MonthlyBillChart({ resp, selectedServicePoint }: BillChartProps) {
  const intl = useIntl();
  const theme = useTheme();
  const userLocaleRaw = React.useRef(IntlService.UserLocaleRaw);
  const renderTotalPrice = React.useMemo(() => (value: number) => Intl.NumberFormat(userLocaleRaw.current, {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0,
  }).format(value), [userLocaleRaw]);

  const [key, setKey] = React.useState(0);

  useEffect(() => {
    const handleResize = () => setKey((prevKey) => prevKey + 1);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const isSwitchAllowed = useCallback(
    (cardContent: BillEstimateResponse) => !(
      selectedServicePoint?.currentPlanCode !== cardContent.planCode && !cardContent.isSwitchAllowed),
    [selectedServicePoint?.currentPlanCode],
  );

  const getChartData = useCallback(() => {
    if (resp) {
      const filteredValidPlans = resp.filter((x) => isSwitchAllowed(x));
      const planCodes = filteredValidPlans.map((item: any) => item.planCode);
      const months = resp.map((item: any) => item.charges.map((elem: any) => intl.formatMessage({ id: `${elem.month}`.toLowerCase() })));
      const totalCharges = filteredValidPlans.map((item: any) => item.charges.map((elem: any) => elem.total));

      const dataSetList = [];
      for (let i = 0; i < planCodes.length; i += 1) {
        const bgColor = mapPlanToPalette(planCodes[i]);
        const colorString = theme.palette[bgColor].main;
        const dataSet = {
          label: intl.formatMessage({ id: `bill-chart.${planCodes[i]}` }),
          data: totalCharges[i],
          backgroundColor: colorString,
          borderWidth: 2,
          borderRadius: Number.MAX_VALUE,
          borderSkipped: false,
          barThickness: 12,
        };
        dataSetList[i] = dataSet;
      }

      setChartData({
        labels: months[0],
        datasets: dataSetList,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resp]);

  useEffect(() => {
    getChartData();
  }, [getChartData]);

  const [chartData, setChartData] = useState<ChartData<'bar', number[], string>>({
    labels: [],
    datasets: [
      {
        label: 'Months',
        data: [],
        backgroundColor: theme.palette.bluePlan.main,
      },
    ],
  });

  return (
    <ChartWrapper data-testid="bar-chart">
      <ChartAreaWrapper>
        <ChartInnerAreaWrapper>
          <Bar
            aria-label="Rate Plan Comparison Chart"
            data={chartData}
            key={key}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: true,
                  position: 'bottom',
                },
                tooltip: {
                  callbacks: {
                    label(context) {
                      let label = context.dataset.label || '';

                      if (label) {
                        label += ': ';
                      }
                      if (context.parsed.y !== null) {
                        label += renderTotalPrice(context.parsed.y);
                      }
                      return label;
                    },
                  },
                },
              },
              scales: {
                x: {
                  grid: {
                    display: false,
                    drawBorder: false,
                  },
                },
                y: {
                  grid: {
                    display: false,
                    drawBorder: false,
                  },
                  ticks: {
                    callback(tickValue, index, ticks) {
                      return `$${tickValue}`;
                    },
                  },
                },
              },
            }}
          />
        </ChartInnerAreaWrapper>
      </ChartAreaWrapper>
    </ChartWrapper>
  );
}

export { MonthlyBillChart };
